export const MAX_VERIFICATION_FAILURES = 5
export const VERIFICATION_LOCK_DURATION_MS = 5 * 60 * 1000
const PHONE_VERIFICATION_LOCK_STORAGE_KEY = 'phone-verification-lock'

type VerificationFailureResult = {
  blockedUntil: number | null
  failedAttempts: number
  isBlocked: boolean
}

export type PhoneVerificationLockState = {
  blockedUntil: number | null
  failedAttempts: number
}

export const getNextVerificationFailureState = (
  failedAttempts: number,
  now: number
): VerificationFailureResult => {
  const nextFailedAttempts = failedAttempts + 1

  if (nextFailedAttempts >= MAX_VERIFICATION_FAILURES) {
    return {
      failedAttempts: nextFailedAttempts,
      blockedUntil: now + VERIFICATION_LOCK_DURATION_MS,
      isBlocked: true,
    }
  }

  return {
    failedAttempts: nextFailedAttempts,
    blockedUntil: null,
    isBlocked: false,
  }
}

export const isVerificationBlocked = (blockedUntil: number | null, now: number) =>
  !!blockedUntil && blockedUntil > now

export const getRemainingVerificationAttempts = (failedAttempts: number) =>
  Math.max(0, MAX_VERIFICATION_FAILURES - failedAttempts)

export const getRemainingLockTimeMs = (blockedUntil: number | null, now: number) =>
  Math.max(0, (blockedUntil || 0) - now)

export const formatRemainingLockTime = (remainingLockTimeMs: number) => {
  const totalSeconds = Math.ceil(remainingLockTimeMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return {
    minutes,
    seconds,
  }
}

const getPhoneVerificationLockStorageItemKey = (tracking?: string) =>
  `${PHONE_VERIFICATION_LOCK_STORAGE_KEY}:${tracking || 'unknown'}`

export const getInitialPhoneVerificationLockState = (
  tracking: string | undefined,
  now: number
): PhoneVerificationLockState => {
  if (typeof window === 'undefined') {
    return {
      blockedUntil: null,
      failedAttempts: 0,
    }
  }

  try {
    const storage = window.localStorage
    const key = getPhoneVerificationLockStorageItemKey(tracking)
    const savedValue = storage.getItem(key)

    if (savedValue) {
      try {
        const parsedValue = JSON.parse(savedValue) as Partial<PhoneVerificationLockState>
        const blockedUntil = typeof parsedValue.blockedUntil === 'number' ? parsedValue.blockedUntil : null
        const failedAttempts = typeof parsedValue.failedAttempts === 'number' ? parsedValue.failedAttempts : 0

        if (isVerificationBlocked(blockedUntil, now)) {
          return { blockedUntil, failedAttempts }
        }
      } catch {
        // Discard malformed saved state using the guarded cleanup below.
      }

      storage.removeItem(key)
    }
  } catch {
    // Storage may be denied by the document's privacy or sandbox settings.
  }

  return {
    blockedUntil: null,
    failedAttempts: 0,
  }
}

export const persistPhoneVerificationLockState = (
  tracking: string | undefined,
  state: PhoneVerificationLockState
) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const storage = window.localStorage
    const key = getPhoneVerificationLockStorageItemKey(tracking)

    if (!state.blockedUntil && state.failedAttempts === 0) {
      storage.removeItem(key)
      return
    }

    storage.setItem(key, JSON.stringify(state))
  } catch {
    //Keep the current React state when persistence is unavailable.
  }
}
